import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import {
  User, Heart, Upload, CheckCircle, Loader2, FileText,
  Phone, Mail, MapPin, Camera, AlertTriangle, IndianRupee
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const UPI_QR_URL = "https://customer-assets.emergentagent.com/job_337dba5c-8d5d-4e7d-adac-9993bcb0c3e3/artifacts/uxuej4sd_WhatsApp%20Image%202026-02-15%20at%2013.14.34.jpeg";

const CATEGORIES = [
  { value: "Mahasabha Padadhikari", label: "Mahasabha Padadhikari / महासभा पदाधिकारी", fee: 2500 },
  { value: "Karyakarini Sadasya", label: "Karyakarini Sadasya / कार्यकारिणी सदस्य", fee: 2500 },
  { value: "Samaj Adhyaksh", label: "Samaj Adhyaksh / समाज अध्यक्ष", fee: 2500 },
  { value: "Mahila Swayamsevak", label: "Mahila Swayamsevak / महिला स्वयंसेवक", fee: 500 },
  { value: "Swayamsevak", label: "Swayamsevak / स्वयंसेवक", fee: 500 },
  { value: "Sangam Mitra", label: "Sangam Mitra / संगम मित्र", fee: 0 },
  { value: "Guest", label: "Guest / अतिथि", fee: 0 },
  { value: "Sangam Mitra Guest", label: "Sangam Mitra Guest / संगम मित्र अतिथि", fee: 0 },
  { value: "Guruji", label: "Guruji / गुरुजी", fee: 0 },
  { value: "Medical", label: "Medical / चिकित्सा", fee: 0 },
  { value: "Rishta Samanvayak", label: "Rishta Samanvayak / रिश्ता समन्वयक", fee: 2500 },
  { value: "Sanyojak", label: "Sanyojak / संयोजक", fee: 2500 },
];

const PSGuestVolunteerRegisterPage = () => {
  const [options, setOptions] = useState({ nukhs: [], tshirtSizes: [], departments: [], availabilities: [], bloodGroups: [] });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [form, setForm] = useState({
    fullName: '', fatherOrHusbandName: '', nukh: '', currentPlace: '',
    whatsapp: '', email: '', category: '',
    localUnit: '', designation: '',
    tshirtSize: '', preferredDepartment: '', availability: [],
    organization: '', guestDesignation: '',
    bloodGroup: '', emergencyContact: '',
    paymentMode: '', transactionId: '',
  });

  const [profilePhoto, setProfilePhoto] = useState(null);
  const [profilePreview, setProfilePreview] = useState(null);
  const [paymentProof, setPaymentProof] = useState(null);
  const profilePhotoRef = useRef(null);
  const paymentProofRef = useRef(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/ps/guest-volunteer/options`)
      .then(r => r.json())
      .then(d => setOptions(d))
      .catch(() => {});
  }, []);

  const selectedCategory = CATEGORIES.find(c => c.value === form.category);
  const isPaid = selectedCategory && selectedCategory.fee > 0;
  const section = selectedCategory?.section || '';

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAvailabilityToggle = (val) => {
    setForm(prev => ({
      ...prev,
      availability: prev.availability.includes(val)
        ? prev.availability.filter(v => v !== val)
        : [...prev.availability, val],
    }));
  };

  const handleProfilePhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Photo must be less than 5MB'); return; }
    setProfilePhoto(file);
    setProfilePreview(URL.createObjectURL(file));
  };

  const handlePaymentProof = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File must be less than 5MB'); return; }
    setPaymentProof(file);
  };

  const validate = () => {
    if (!form.fullName.trim()) { toast.error('Full Name is required / पूरा नाम आवश्यक है'); return false; }
    if (!form.fatherOrHusbandName.trim()) { toast.error("Father's / Husband's Name is required"); return false; }
    if (!form.currentPlace.trim()) { toast.error('Current Place/City is required'); return false; }
    if (!/^\d{10}$/.test(form.whatsapp.trim())) { toast.error('WhatsApp must be exactly 10 digits'); return false; }
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { toast.error('Valid email is required'); return false; }
    if (!form.category) { toast.error('Please select a Registration Category'); return false; }

    if (!profilePhoto) { toast.error('Profile Photo is required for ID Card'); return false; }

    if (isPaid) {
      if (!form.paymentMode) { toast.error('Please select a payment mode'); return false; }
      if (!form.transactionId.trim()) { toast.error('Transaction ID / UTR Number is required'); return false; }
      if (!paymentProof) { toast.error('Payment screenshot is required'); return false; }
    }

    if (!termsAccepted) { toast.error('Please accept the terms / कृपया नियम स्वीकार करें'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);

    try {
      const fd = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        if (key === 'availability') {
          fd.append(key, Array.isArray(val) ? val.join(',') : '');
        } else {
          fd.append(key, val);
        }
      });
      if (profilePhoto) fd.append('profilePhoto', profilePhoto);
      if (paymentProof) fd.append('paymentProof', paymentProof);

      const res = await fetch(`${BACKEND_URL}/api/ps/guest-volunteer/register`, {
        method: 'POST',
        body: fd,
      });

      if (res.ok) {
        const data = await res.json();
        setResult(data);
        setSubmitted(true);
        toast.success('Registration successful!');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted && result) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-12">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
          <Card className="max-w-lg w-full p-8 text-center" data-testid="gv-registration-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registration Successful!</h2>
            <p className="text-gray-600 mb-4">पंजीकरण सफल!</p>
            <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2 mb-4">
              <p className="text-sm"><span className="text-gray-500">Name:</span> <span className="font-bold">{result.fullName}</span></p>
              <p className="text-sm"><span className="text-gray-500">Category:</span> <span className="font-bold">{result.category}</span></p>
              <p className="text-sm"><span className="text-gray-500">Registration Code:</span> <span className="font-bold font-mono text-lg text-primary">{result.registrationCode}</span></p>
              {result.fee > 0 && (
                <p className="text-sm"><span className="text-gray-500">Fee:</span> <span className="font-bold">{'\u20B9'}{result.fee.toLocaleString()}</span></p>
              )}
              <p className="text-sm"><span className="text-gray-500">Status:</span> <span className="font-bold text-yellow-600">Pending Approval</span></p>
            </div>
            <p className="text-sm text-gray-500">Please save your registration code. Admin will review and approve your registration. Your ID Card will be generated once approved.</p>
            <p className="text-sm text-gray-500 mt-2">कृपया अपना पंजीकरण कोड सेव करें। प्रशासक आपके पंजीकरण की समीक्षा करेगा। स्वीकृति के बाद आपका ID Card जारी किया जाएगा।</p>
          </Card>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="gradient-orange py-10">
        <div className="max-w-4xl mx-auto px-4">
          <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">Parichay Sangam 2026</h1>
          <p className="text-lg text-white/90">Guest & Volunteer Registration / अतिथि एवं स्वयंसेवक पंजीकरण</p>
        </div>
      </section>

      <form onSubmit={handleSubmit} className="max-w-4xl mx-auto px-4 py-8">
        {/* Instructions */}
        <Card className="p-5 mb-6 bg-amber-50 border-amber-200">
          <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Important Instructions / महत्वपूर्ण निर्देश
          </h3>
          <ul className="text-sm text-amber-800 space-y-1 list-disc list-inside">
            <li>All fields marked with <span className="text-destructive font-bold">*</span> are mandatory / सभी <span className="text-destructive font-bold">*</span> वाले फील्ड अनिवार्य हैं</li>
            <li>Profile photo is required for ID Card generation / ID Card के लिए फोटो अनिवार्य है</li>
            <li>For paid categories, complete payment before submitting / भुगतान श्रेणियों के लिए पहले भुगतान करें</li>
            <li>Each person can register only once / प्रत्येक व्यक्ति केवल एक बार पंजीकरण कर सकता है</li>
          </ul>
        </Card>

        {/* Section 1: Core Personal Details */}
        <Card className="p-6 mb-6" data-testid="gv-section-personal">
          <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
            <User className="w-5 h-5 text-primary" /> Personal Details / व्यक्तिगत विवरण
          </h3>
          <p className="text-sm text-gray-500 mb-5">Section 1: Required for all categories</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm">Full Name / पूरा नाम <span className="text-destructive">*</span></Label>
              <Input value={form.fullName} onChange={(e) => handleChange('fullName', e.target.value)} placeholder="Enter full name" className="mt-1" data-testid="gv-fullName" />
            </div>
            <div>
              <Label className="text-sm">Father's / Husband's Name / पिता / पति का नाम <span className="text-destructive">*</span></Label>
              <Input value={form.fatherOrHusbandName} onChange={(e) => handleChange('fatherOrHusbandName', e.target.value)} placeholder="Enter name" className="mt-1" data-testid="gv-fatherName" />
            </div>
            <div>
              <Label className="text-sm">Nukh / नुख</Label>
              <select value={form.nukh} onChange={(e) => handleChange('nukh', e.target.value)} className="w-full h-10 px-3 rounded-md border text-sm mt-1" data-testid="gv-nukh">
                <option value="">Select Nukh</option>
                {options.nukhs?.map((n, i) => <option key={i} value={n}>{n}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-sm">Current Place / City / वर्तमान शहर <span className="text-destructive">*</span></Label>
              <Input value={form.currentPlace} onChange={(e) => handleChange('currentPlace', e.target.value)} placeholder="e.g. Jodhpur" className="mt-1" data-testid="gv-currentPlace" />
            </div>
            <div>
              <Label className="text-sm">WhatsApp Mobile Number / व्हाट्सएप नंबर <span className="text-destructive">*</span></Label>
              <Input value={form.whatsapp} onChange={(e) => handleChange('whatsapp', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10 digit number" className="mt-1" data-testid="gv-whatsapp" />
            </div>
            <div>
              <Label className="text-sm">Email Address / ईमेल <span className="text-destructive">*</span></Label>
              <Input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="email@example.com" className="mt-1" data-testid="gv-email" />
            </div>
          </div>

          {/* Category Selection */}
          <div className="mt-6">
            <Label className="text-sm font-semibold">Registration Category / पंजीकरण श्रेणी <span className="text-destructive">*</span></Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
              {CATEGORIES.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => handleChange('category', cat.value)}
                  className={`text-left p-3 rounded-lg border-2 transition-all ${
                    form.category === cat.value
                      ? 'border-primary bg-primary/5 ring-1 ring-primary'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`gv-category-${cat.value.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <p className="font-medium text-sm text-gray-900">{cat.label}</p>
                  <p className={`text-xs mt-1 font-semibold ${cat.fee > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    {cat.fee > 0 ? `₹${cat.fee.toLocaleString()}` : 'Free / निःशुल्क'}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </Card>

        {/* Section 5: ID Card & Payment */}
        {form.category && (
          <Card className="p-6 mb-6" data-testid="gv-section-idcard-payment">
            <h3 className="text-lg font-bold text-gray-900 mb-1 flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" /> ID Card & Payment / आईडी कार्ड एवं भुगतान
            </h3>
            <p className="text-sm text-gray-500 mb-5">Section 5: Photo, payment and emergency details</p>

            {/* Profile Photo Upload */}
            <div className="mb-5">
              <Label className="text-sm font-semibold">Profile Photo / फोटो <span className="text-destructive">*</span></Label>
              <p className="text-xs text-gray-500 mb-2">Clear, front-facing photo with plain background for ID Card / ID Card के लिए सामने से खींची गई स्पष्ट फोटो</p>
              <div className="flex items-center gap-4 mt-1">
                <button
                  type="button"
                  onClick={() => profilePhotoRef.current?.click()}
                  className={`flex items-center gap-2 px-4 py-3 border-2 border-dashed rounded-lg text-sm transition-colors ${
                    profilePhoto ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-primary hover:bg-orange-50'
                  }`}
                  data-testid="gv-profile-photo-upload"
                >
                  {profilePhoto ? (
                    <><CheckCircle className="w-4 h-4 text-green-600" /><span className="text-green-700 font-medium truncate max-w-[200px]">{profilePhoto.name}</span></>
                  ) : (
                    <><Upload className="w-4 h-4 text-gray-400" /><span className="text-gray-500">Upload Photo (JPG, PNG, WEBP, max 5MB)</span></>
                  )}
                </button>
                <input ref={profilePhotoRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleProfilePhoto} />
                {profilePreview && (
                  <img src={profilePreview} alt="Preview" className="w-16 h-16 object-cover rounded-lg border" />
                )}
              </div>
            </div>

            {/* Payment Section (for paid categories) */}
            {isPaid && (
              <div className="mb-5 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <h4 className="font-semibold text-orange-800 mb-1 flex items-center gap-2">
                  <IndianRupee className="w-4 h-4" /> Payment: {'\u20B9'}{selectedCategory.fee.toLocaleString()}
                </h4>
                <p className="text-xs text-orange-700 mb-3">Please complete payment before submitting / कृपया सबमिट करने से पहले भुगतान करें</p>

                <div className="flex gap-3 mb-3">
                  {['UPI', 'Bank Transfer'].map(mode => (
                    <Button
                      key={mode}
                      type="button"
                      variant={form.paymentMode === mode ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => handleChange('paymentMode', mode)}
                      className={form.paymentMode === mode ? 'bg-orange-600 hover:bg-orange-700' : ''}
                      data-testid={`gv-payment-${mode.toLowerCase().replace(' ', '-')}`}
                    >
                      {mode}
                    </Button>
                  ))}
                </div>

                {form.paymentMode === 'UPI' && (
                  <div className="bg-white p-4 rounded-lg border border-blue-200 mb-3">
                    <h5 className="font-semibold text-blue-800 mb-3 text-center">UPI से भुगतान करें / Pay via UPI</h5>
                    <div className="flex flex-col items-center">
                      <img src={UPI_QR_URL} alt="UPI QR Code" className="max-w-[200px] w-full h-auto rounded-lg shadow-md mb-3" />
                      <p className="text-sm text-blue-700 font-medium">UPI ID: boim-661059350985@boi</p>
                      <p className="text-xs text-blue-600 mt-1">किसी भी UPI ऐप से स्कैन करें / Scan with any UPI App</p>
                    </div>
                  </div>
                )}

                {form.paymentMode === 'Bank Transfer' && (
                  <div className="bg-white p-4 rounded-lg border border-green-200 mb-3">
                    <h5 className="font-semibold text-green-800 mb-2">Bank Details / बैंक विवरण</h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                      <div><span className="font-medium text-green-700">Account Name:</span><p className="text-gray-800">BRAHMAKSHATRIYA MARVADI KHATRI ASSOCIATION</p></div>
                      <div><span className="font-medium text-green-700">Bank:</span><p className="text-gray-800">BANK OF INDIA</p></div>
                      <div><span className="font-medium text-green-700">Account No:</span><p className="text-gray-800 font-mono select-all">661020110000985</p></div>
                      <div><span className="font-medium text-green-700">IFSC Code:</span><p className="text-gray-800 font-mono select-all">BKID0006610</p></div>
                      <div><span className="font-medium text-green-700">Branch:</span><p className="text-gray-800">JODHPUR, RAJASTHAN</p></div>
                      <div><span className="font-medium text-green-700">MICR Code:</span><p className="text-gray-800 font-mono">342013002</p></div>
                    </div>
                  </div>
                )}

                {form.paymentMode && (
                  <>
                    <div className="mb-3">
                      <Label className="text-sm">Transaction ID / UTR Number <span className="text-destructive">*</span></Label>
                      <Input value={form.transactionId} onChange={(e) => handleChange('transactionId', e.target.value)} placeholder="Enter UTR / Transaction Reference" className="mt-1" data-testid="gv-transactionId" />
                    </div>
                    <div>
                      <Label className="text-sm">Payment Screenshot <span className="text-destructive">*</span></Label>
                      <button
                        type="button"
                        onClick={() => paymentProofRef.current?.click()}
                        className={`flex items-center gap-2 w-full px-4 py-3 border-2 border-dashed rounded-lg text-sm mt-1 transition-colors ${
                          paymentProof ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-orange-400 hover:bg-orange-50'
                        }`}
                        data-testid="gv-payment-proof-upload"
                      >
                        {paymentProof ? (
                          <><CheckCircle className="w-4 h-4 text-green-600" /><span className="text-green-700 font-medium">{paymentProof.name}</span></>
                        ) : (
                          <><FileText className="w-4 h-4 text-gray-400" /><span className="text-gray-500">Upload payment screenshot (JPG, PNG, PDF, max 5MB)</span></>
                        )}
                      </button>
                      <input ref={paymentProofRef} type="file" accept="image/jpeg,image/png,image/webp,application/pdf" className="hidden" onChange={handlePaymentProof} />
                    </div>
                  </>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Terms & Submit */}
        {form.category && (
          <Card className="p-6 mb-6">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="gv-terms-checkbox">
              <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} className="w-5 h-5 mt-0.5 rounded border-gray-300 text-primary" />
              <span className="text-sm text-gray-700">
                मैं यह पुष्टि करता/करती हूँ कि मेरे द्वारा दी गई सभी जानकारी सत्य है। / I confirm that all the information provided by me is true and correct.
              </span>
            </label>

            <Button
              type="submit"
              disabled={submitting || !termsAccepted}
              className="w-full py-6 text-lg bg-orange-600 hover:bg-orange-700 disabled:opacity-50 mt-4"
              data-testid="gv-submit-btn"
            >
              {submitting ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Submitting...</> : 'Register / पंजीकरण करें'}
            </Button>
          </Card>
        )}
      </form>
    </div>
  );
};

export default PSGuestVolunteerRegisterPage;
